import { useCallback, useEffect, useId, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { PERMISSION_SET_REFUSAL } from '@/lib/permissions';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import type {
  ExchangeRateProviderKey,
  ExchangeRateSettings as Settings,
  ExchangeRateSettingsRefreshResult,
  ExchangeRateSettingsUpdateInput,
} from '@/lib/types';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Select,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * EXCHANGE-RATE SETTINGS — `GET | PATCH /settings/exchange-rates` and
 * `POST /settings/exchange-rates/refresh` (owner, 2026-10-28).
 *
 * - PATCH sends only the keys that changed; an empty patch is a 422, so Save is
 *   disabled while nothing differs from what was loaded.
 * - `fallbackProvider: null` = no fallback; `translationProvider: null` = same
 *   as the daily provider; `backfillFrom: null` = cleared. `''` is never sent.
 * - A provider the server reports `configured: false` is a disabled option,
 *   with the reason beside the controls (the why-disabled exception to the
 *   no-helper-text rule). A stored value is never disabled, so it stays visible.
 * - Gated on `ability.isAdmin`, the tier every `/settings/*` write is guarded
 *   by. The 403 is still handled: the gate is the optimisation, not the guarantee.
 * - `xero` (owner 2026-10-28) is a HARVESTED provider: rates come from the
 *   CurrencyRate on Xero documents. The server's `providers` list drives the
 *   options; `xero` is appended with its default label only when an older API
 *   omits it. It is offered for daily and fallback, never for translation
 *   (422 `FX_XERO_NOT_FOR_TRANSLATION` marks that control); 422
 *   `FX_XERO_NEEDS_FALLBACK` marks the Fallback control. The
 *   refresh result's `xeroHarvest` (`undefined` = older API, `null` = no harvest
 *   ran) is summarised in the same result notice.
 */

const XERO_PROVIDER_LABEL = 'Xero (rates on Xero documents)';

/**
 * A rate SOURCE as the facts and the refresh result name it; the long provider
 * label stays in the dropdowns. A legacy source shows as stored.
 */
const SOURCE_SHORT_NAMES: Record<string, string> = { seed: 'SEED', xe: 'XE', xero: 'Xero' };
const sourceName = (source: string) => SOURCE_SHORT_NAMES[source] ?? source;

/** Harvest document types as the summary names them; an unknown key shows as sent. */
const HARVEST_TYPE_LABELS: Record<string, string> = {
  invoice: 'Invoices',
  'credit-note': 'Credit notes',
  payment: 'Payments',
  'bank-transaction': 'Bank transactions',
  overpayment: 'Overpayments',
  prepayment: 'Prepayments',
};

const HARVEST_STATUS_MESSAGES: Record<string, string> = {
  'not-connected': 'Xero rates were not read: Xero is not connected.',
  'base-currency-mismatch': 'Xero rates were not read: the Xero organisation’s base currency is not GBP.',
};

type XeroHarvest = NonNullable<ExchangeRateSettingsRefreshResult['xeroHarvest']>;

/** True when the harvest needs attention: it did not run cleanly, or a type failed. */
function harvestNeedsAttention(harvest: XeroHarvest | null | undefined): boolean {
  if (!harvest) return false;
  if (harvest.status !== 'ok') return true;
  return Object.values(harvest.types ?? {}).some((t) => t.status === 'failed');
}

function XeroHarvestSummary({ harvest }: { harvest: XeroHarvest }) {
  if (harvest.status !== 'ok') {
    return (
      <p className="m-0 [overflow-wrap:anywhere]">
        {HARVEST_STATUS_MESSAGES[harvest.status] ?? `Xero rates were not read: ${harvest.status}.`}
      </p>
    );
  }
  const types = Object.entries(harvest.types ?? {});
  const labelOf = (key: string) => HARVEST_TYPE_LABELS[key] ?? key;
  const read = types.filter(([, t]) => t.status === 'ok');
  const unavailable = types.filter(([, t]) => t.status === 'unavailable');
  const failed = types.filter(([, t]) => t.status === 'failed');
  const written = harvest.ratesWritten ?? 0;
  const removed = harvest.ratesDeleted ?? 0;
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      <li>{`Xero rates: ${written} written${removed > 0 ? `, ${removed} removed` : ''}.`}</li>
      {read.length > 0 && (
        <li className="[overflow-wrap:anywhere]">
          {`Read: ${read.map(([key, t]) => `${labelOf(key)} ${t.fetched}`).join(', ')}.`}
        </li>
      )}
      {unavailable.length > 0 && (
        <li className="[overflow-wrap:anywhere]">
          {`Unavailable: ${unavailable.map(([key]) => labelOf(key)).join(', ')}.`}
        </li>
      )}
      {failed.map(([key, t]) => (
        <li key={key} className="[overflow-wrap:anywhere]">
          {`${labelOf(key)} failed${t.error ? `: ${t.error}` : '.'}`}
        </li>
      ))}
    </ul>
  );
}

/** The write keys, so a 422 naming one of them marks its control. */
const KNOWN_KEYS = [
  'dailyProvider',
  'fallbackProvider',
  'useXeroDocumentRates',
  'translationProvider',
  'refreshIntervalHours',
  'backfillFrom',
] as const;

type KnownKey = (typeof KNOWN_KEYS)[number];

const MIN_HOURS = 1;
const MAX_HOURS = 24;

interface Draft {
  dailyProvider: ExchangeRateProviderKey;
  /** `''` = None. */
  fallbackProvider: '' | ExchangeRateProviderKey;
  /** `''` = Same as daily. */
  translationProvider: '' | ExchangeRateProviderKey;
  useXeroDocumentRates: boolean;
  refreshIntervalHours: string;
  /** `''` = not set. */
  backfillFrom: string;
}

function draftOf(settings: Settings): Draft {
  return {
    dailyProvider: settings.dailyProvider,
    fallbackProvider: settings.fallbackProvider ?? '',
    translationProvider: settings.translationProvider ?? '',
    useXeroDocumentRates: settings.useXeroDocumentRates,
    refreshIntervalHours: String(settings.refreshIntervalHours),
    backfillFrom: settings.backfillFrom ?? '',
  };
}

/** The interval as a whole number of hours in range, or `null` when it is not one. */
function hoursOf(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= MIN_HOURS && value <= MAX_HOURS ? value : null;
}

function buildPatch(settings: Settings, draft: Draft): ExchangeRateSettingsUpdateInput {
  const patch: ExchangeRateSettingsUpdateInput = {};
  if (draft.dailyProvider !== settings.dailyProvider) patch.dailyProvider = draft.dailyProvider;
  const fallback = draft.fallbackProvider === '' ? null : draft.fallbackProvider;
  if (fallback !== settings.fallbackProvider) patch.fallbackProvider = fallback;
  const translation = draft.translationProvider === '' ? null : draft.translationProvider;
  if (translation !== settings.translationProvider) patch.translationProvider = translation;
  if (draft.useXeroDocumentRates !== settings.useXeroDocumentRates) {
    patch.useXeroDocumentRates = draft.useXeroDocumentRates;
  }
  const hours = hoursOf(draft.refreshIntervalHours);
  if (hours !== null && hours !== settings.refreshIntervalHours) {
    patch.refreshIntervalHours = hours;
  }
  const backfill = draft.backfillFrom === '' ? null : draft.backfillFrom;
  if (backfill !== settings.backfillFrom) patch.backfillFrom = backfill;
  return patch;
}

export function ExchangeRateSettings() {
  const { ability } = useAuth();
  const headingId = useId();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<KnownKey, string>>>({});

  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<ExchangeRateSettingsRefreshResult | null>(
    null,
  );
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    return api
      .getExchangeRateSettings(signal)
      .then((response) => {
        if (signal.aborted) return;
        setSettings(response.data);
        setDraft(draftOf(response.data));
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setLoadError(caught instanceof Error ? caught.message : 'The settings could not be read.');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const denial = ability.isAdmin ? null : PERMISSION_SET_REFUSAL;
  const locked = denial !== null || saving;

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setSaved(false);
    setSaveError(null);
    setFieldErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key as KnownKey];
      return next;
    });
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  function save(patch: ExchangeRateSettingsUpdateInput) {
    if (saving) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    setFieldErrors({});
    api
      .updateExchangeRateSettings(patch)
      .then((response) => {
        setSettings(response.data);
        setDraft(draftOf(response.data));
        setSaved(true);
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError) {
          const routed = caught.routeFieldErrors([...KNOWN_KEYS]);
          if (caught.code === 'FX_XERO_NEEDS_FALLBACK' && !routed.fields.fallbackProvider) {
            routed.fields.fallbackProvider = caught.message;
          }
          if (caught.code === 'FX_XERO_NOT_FOR_TRANSLATION' && !routed.fields.translationProvider) {
            routed.fields.translationProvider = caught.message;
          }
          setFieldErrors(routed.fields as Partial<Record<KnownKey, string>>);
          const extra = [...caught.formMessages, ...routed.unroutable];
          if (extra.length > 0) setSaveError(extra.join(' '));
          else if (Object.keys(routed.fields).length === 0) setSaveError(caught.message);
          return;
        }
        setSaveError(caught instanceof Error ? caught.message : 'The settings were not saved.');
      })
      .finally(() => setSaving(false));
  }

  function fetchNow() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshResult(null);
    setRefreshError(null);
    api
      .refreshExchangeRatesNow()
      .then((response) => {
        setRefreshResult(response.data);
        // The latest-rate facts move with a fetch; re-read them.
        const controller = new AbortController();
        void load(controller.signal);
      })
      .catch((caught: unknown) => {
        setRefreshError(caught instanceof Error ? caught.message : 'Rates were not fetched.');
      })
      .finally(() => setRefreshing(false));
  }

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Exchange rates" />
      <PanelBody className="flex flex-col gap-4">
        {loadError && (
          <Notice tone="danger" role="alert" title="Couldn’t read the exchange-rate settings.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{loadError}</span>
          </Notice>
        )}

        {loading && settings === null && !forbidden ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-40" />
          </div>
        ) : forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">{PERMISSION_SET_REFUSAL}</p>
        ) : settings === null || draft === null ? null : (
          <SettingsForm
            settings={settings}
            draft={draft}
            locked={locked}
            denial={denial}
            saving={saving}
            saved={saved}
            saveError={saveError}
            fieldErrors={fieldErrors}
            refreshing={refreshing}
            refreshResult={refreshResult}
            refreshError={refreshError}
            onChange={update}
            onSave={save}
            onFetch={fetchNow}
          />
        )}
      </PanelBody>
    </Panel>
  );
}

function SettingsForm({
  settings,
  draft,
  locked,
  denial,
  saving,
  saved,
  saveError,
  fieldErrors,
  refreshing,
  refreshResult,
  refreshError,
  onChange,
  onSave,
  onFetch,
}: {
  settings: Settings;
  draft: Draft;
  locked: boolean;
  denial: string | null;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
  fieldErrors: Partial<Record<KnownKey, string>>;
  refreshing: boolean;
  refreshResult: ExchangeRateSettingsRefreshResult | null;
  refreshError: string | null;
  onChange: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  onSave: (patch: ExchangeRateSettingsUpdateInput) => void;
  onFetch: () => void;
}) {
  const providers = settings.providers.some((p) => p.key === 'xero')
    ? settings.providers
    : [...settings.providers, { key: 'xero' as const, label: XERO_PROVIDER_LABEL, configured: true }];
  const unconfigured = providers.filter((p) => !p.configured);

  /** A provider is offered when configured, or when it is the value already stored. */
  function providerOptions(stored: ExchangeRateProviderKey | null, offerXero = true) {
    // Xero is never offered for translation (422 FX_XERO_NOT_FOR_TRANSLATION); a stored one stays visible.
    const offered = offerXero ? providers : providers.filter((p) => p.key !== 'xero' || p.key === stored);
    return offered.map((p) => (
      <option key={p.key} value={p.key} disabled={!p.configured && p.key !== stored}>
        {p.configured ? p.label : `${p.label} (not configured)`}
      </option>
    ));
  }

  const hours = hoursOf(draft.refreshIntervalHours);
  const hoursError =
    fieldErrors.refreshIntervalHours ??
    (hours === null ? `Enter a whole number from ${MIN_HOURS} to ${MAX_HOURS}.` : undefined);
  const patch = buildPatch(settings, draft);
  const changed = Object.keys(patch).length > 0;
  const saveDisabled = locked || !changed || hours === null;
  const saveReason = denial ?? (hours === null ? 'Fix the refresh interval to save.' : null);

  const sources = Object.entries(settings.latestRateDates);

  return (
    <>
      {saveError && (
        <Notice tone="danger" role="alert" title="Couldn’t save the exchange-rate settings.">
          <span className="min-w-0 [overflow-wrap:anywhere]">{saveError}</span>
        </Notice>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Daily rate source" error={fieldErrors.dailyProvider}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.dailyProvider}
              disabled={locked}
              invalid={Boolean(fieldErrors.dailyProvider)}
              onChange={(event) =>
                onChange('dailyProvider', event.target.value as ExchangeRateProviderKey)
              }
            >
              {providerOptions(settings.dailyProvider)}
            </Select>
          )}
        </Field>

        <Field label="Fallback source" error={fieldErrors.fallbackProvider}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.fallbackProvider}
              disabled={locked}
              invalid={Boolean(fieldErrors.fallbackProvider)}
              onChange={(event) =>
                onChange('fallbackProvider', event.target.value as Draft['fallbackProvider'])
              }
            >
              <option value="">None</option>
              {providerOptions(settings.fallbackProvider)}
            </Select>
          )}
        </Field>

        <Field label="Period-end translation source" error={fieldErrors.translationProvider}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={draft.translationProvider}
              disabled={locked}
              invalid={Boolean(fieldErrors.translationProvider)}
              onChange={(event) =>
                onChange('translationProvider', event.target.value as Draft['translationProvider'])
              }
            >
              <option value="">Same as daily</option>
              {providerOptions(settings.translationProvider, false)}
            </Select>
          )}
        </Field>

        <Field label="Refresh every (hours)" error={hoursError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="numeric"
              min={MIN_HOURS}
              max={MAX_HOURS}
              step={1}
              value={draft.refreshIntervalHours}
              disabled={locked}
              invalid={Boolean(hoursError)}
              onChange={(event) => onChange('refreshIntervalHours', event.target.value)}
            />
          )}
        </Field>

        <Field label="Backfill from" error={fieldErrors.backfillFrom}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              value={draft.backfillFrom}
              disabled={locked}
              invalid={Boolean(fieldErrors.backfillFrom)}
              onChange={(event) => onChange('backfillFrom', event.target.value)}
            />
          )}
        </Field>

        <div className="flex flex-col justify-end gap-1">
          <Checkbox
            label="Use Xero’s rate on each document"
            checked={draft.useXeroDocumentRates}
            disabled={locked}
            onChange={(event) => onChange('useXeroDocumentRates', event.target.checked)}
          />
          {fieldErrors.useXeroDocumentRates && (
            <p role="alert" className="m-0 text-xs text-danger">
              {fieldErrors.useXeroDocumentRates}
            </p>
          )}
        </div>
      </div>

      {unconfigured.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {unconfigured.map((p) => (
            <li key={p.key} className="text-xs text-fg-subtle">
              {`${p.label} credentials are not set on the server.`}
            </li>
          ))}
        </ul>
      )}

      <dl className="m-0 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {sources.map(([source, date]) => (
          <div key={source} className="min-w-0">
            <dt className="text-xs text-fg-subtle">{`Latest rate — ${sourceName(source)}`}</dt>
            <dd className="m-0 text-fg tabular-nums">{formatDateOnly(date)}</dd>
          </div>
        ))}
        <div className="min-w-0">
          <dt className="text-xs text-fg-subtle">Last changed</dt>
          <dd className="m-0 text-fg [overflow-wrap:anywhere]">
            {settings.updatedAt
              ? `${formatDateTime(settings.updatedAt)}${
                  settings.updatedBy ? ` by ${settings.updatedBy.name}` : ''
                }`
              : '—'}
          </dd>
        </div>
      </dl>

      {refreshError && (
        <Notice tone="danger" role="alert" title="Couldn’t fetch rates.">
          <span className="min-w-0 [overflow-wrap:anywhere]">{refreshError}</span>
        </Notice>
      )}
      {refreshResult && (
        <Notice
          tone={
            refreshResult.errors.length > 0 || harvestNeedsAttention(refreshResult.xeroHarvest)
              ? 'warning'
              : 'success'
          }
          role="status"
          title={`${sourceName(refreshResult.provider)} rates for ${formatDateOnly(
            refreshResult.date,
          )}: ${refreshResult.written} written.`}
        >
          {refreshResult.errors.length > 0 && (
            <ul className="m-0 flex list-disc flex-col gap-0.5 pl-4">
              {refreshResult.errors.map((message, index) => (
                <li key={index} className="[overflow-wrap:anywhere]">
                  {message}
                </li>
              ))}
            </ul>
          )}
          {refreshResult.xeroHarvest && <XeroHarvestSummary harvest={refreshResult.xeroHarvest} />}
        </Notice>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        {/* `title` on a wrapper: a disabled Button never receives the hover. */}
        <span title={saveReason ?? undefined}>
          <Button onClick={() => onSave(patch)} disabled={saveDisabled}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </span>
        <span title={denial ?? undefined}>
          <Button
            variant="secondary"
            onClick={onFetch}
            disabled={denial !== null || refreshing}
            loading={refreshing}
          >
            Fetch rates now
          </Button>
        </span>
        {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}
        {saved && !denial && (
          <p role="status" className="m-0 text-xs text-fg-subtle">
            Saved.
          </p>
        )}
      </div>
    </>
  );
}
